/**
 * Where records actually go, as a port with two plugs.
 *
 * Everything worth testing about a save sits above this line: the version gate, the migration
 * chain, the section validation, the staged swap and what happens when a write fails halfway. The
 * unit suite runs in Node, which has no IndexedDB, so the line is not a nicety. It is the reason
 * any of it is testable at all.
 *
 * A write is deliberately two calls rather than one. `stage` puts the whole new record under a
 * staging key and commits that; `commit` then moves it onto the live key and drops the staging
 * record, in one transaction. A crash between the two leaves the previous record whole and an
 * orphan staging record behind, which `openProfileStore` sweeps. A crash inside either one rolls
 * that transaction back. There is no window in which the live key holds half a profile.
 *
 * A backend throws. `profileStore` is the one place that catches, so a backend stays a thin
 * adapter over a storage API rather than a second copy of the failure vocabulary.
 */

/** The prefix a staged record is written under, so a sweep can recognise one. */
export const STAGING_PREFIX = 'staging:';

export interface SaveBackend {
  /** The record under `key`, or null when there is none. */
  read(key: string): Promise<unknown>;
  /** Every key in the store, live and staged alike. */
  keys(): Promise<readonly string[]>;
  /** Writes the whole record under a staging key, and commits that write. */
  stage(key: string, record: unknown): Promise<void>;
  /** One transaction: staging onto live, then staging gone. */
  commit(stagingKey: string, liveKey: string): Promise<void>;
  remove(key: string): Promise<void>;
  close(): void;
}

export function stagingKeyFor(profileId: string): string {
  return `${STAGING_PREFIX}${profileId}`;
}

export function isStagingKey(key: string): boolean {
  return key.startsWith(STAGING_PREFIX);
}

/**
 * Whether a thrown value is the browser saying the store is full.
 *
 * Matched on `name` rather than on `instanceof`, because the quota error is a `DOMException` in
 * every engine and a `DOMException` is not a class this code can rely on being the same one.
 */
export function isQuotaExceeded(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  return 'name' in error && error.name === 'QuotaExceededError';
}

/** A thrown value as one line, for a failure that carries what the storage layer said. */
export function detailOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : 'unknown storage error';
}
